<template>
  <div class="info-wrapper">

    <div v-if="!isWeightValid" class="alert">{{ 'info.weight_overflow' | translate }}</div>

    <div v-if="item.schedule_mixer.deleted" class="alert">
      {{ 'base.mixer' | translate }} "{{ item.schedule_mixer.name }}" {{ 'info.deleted_select_other' | translate }}</div>

    <div v-if="item.schedule_ration.deleted" class="alert">
      {{ 'base.ration' | translate }} "{{ item.schedule_ration.name }}" {{ 'info.deleted_select_other' | translate }}</div>


    <div class="cols">

      <!-- Ration info -->
      <div class="col">
        <div class="col-title">{{ 'base.ration' | translate }}</div>
        <div class="ration-select">
          <v-select
            :disabled="!hasPermission('loading', 'change')"
            :title="'base.ration' | translate"
            v-model="ration"
            :options="rationsList"
            :returnObject="true"
          />
        </div>
        <div class="ration-detail">
          <div class="item">
            <div class="title">{{ 'base.animal_count' | translate }}</div>
            <div class="value">{{ animalCount }} {{ 'base.heads' | translate }}</div>
          </div>
          <div class="item">
            <div class="title">{{ 'base.weight_per_head' | translate }}</div>
            <div class="value">{{ weightPerHead ? weightPerHead.toFixed(2) : '-' }} {{ 'base.kg' | translate }}</div>
          </div>
          <div class="item" :class="{ 'error': !isWeightValid }">
            <div class="title">{{ 'base.load_weight' | translate }}</div>
            <div class="value">{{ totalWeight.toFixed(0) }} {{ 'base.kg' | translate }}</div>
          </div>
          <div class="item" v-if="item.is_adjustment_leftovers" >
            <div class="title">{{ 'base.leftovers_planning' | translate }}</div>
            <div class="value">{{ item.leftovers_percent }} %</div>
          </div>
        </div>
      </div>

      <!-- Ration ingredients list and details -->
      <div class="col">
        <div class="col-title-offset col-title">
          <div>{{ 'base.ration_recipe' | translate }}</div>
          <div class="sub-controls">
            <div class="label">{{ 'base.mix_time' | translate }}</div>
            <div @click="openRationInfoDialog" class="i-util-i"></div>
          </div>
        </div>
        <div class="ration-ingredients">
            <template v-if="item.schedule_ration.ingredients">
              <ingredient-item
                v-for="item in item.schedule_ration.ingredients"
                :key="item.id"
                :item="item"
                @on-change="onChange"/>
            </template>
            <ration-skeleton v-else/>
        </div>
      </div>

      <!-- Upload mixers -->
      <div class="col">
        <div class="col-title">{{ 'base.mixer' | translate }}</div>

        <v-select
          :disabled="!hasPermission('loading', 'change')"
          :title="'base.mixer' | translate"
          v-model="mixer"
          :options="mixers"
          :returnObject="true"
          />

        <div class="ration-detail">
          <div class="item">
            <div class="title">{{ 'base.mix_time' | translate }}</div>
            <ui-number-input
              :value="Math.floor(item.mix_time / 60)"
              @input="update" :postfix="'base.min' | translate"/>
          </div>
        </div>
      </div>

      <!-- Ration ingredients detail dialog -->
      <v-dialog
        max-width="660"
        v-model="showRationInfoDialog"
        @close="closeRationInfoDialog">
        <ration-info-dialog
          :id="item.id"
          @close="closeRationInfoDialog"/>
      </v-dialog>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import UiNumberInput from '@/components/ui/ui-number-input/UiNumberInput.vue'

import IngredientItem from './IngredientItem.vue'
import RationInfoDialog from '../dialogs/RationInfoDialog.vue'

import RationSkeleton from './RationSkeleton'

const focus = {
  inserted (el) {
    el.focus()
  }
}

export default {
  name: 'InfoWrapper',

  props: {
    item: Object
  },

  directives: { focus },

  components: {
    UiNumberInput,
    IngredientItem,
    RationInfoDialog,
    RationSkeleton
  },

  mounted () {
    this.loadData()
    this.mixer = {
      id: this.item.schedule_mixer.mixer,
      name: this.item.schedule_mixer.name,
      max_weight: this.item.schedule_mixer.max_weight
    }
    this.ration = {
      id: this.item.schedule_ration.ration,
      name: this.item.schedule_ration.name,
      ingredients: this.item.schedule_ration.ingredients
    }
  },

  data: () => ({
    ration: null,
    mixer: null,
    mixers: [],
    showRationInfoDialog: false
  }),

  methods: {
    ...mapActions('mixers', [
      'loadMixersList'
    ]),

    ...mapActions('rations', [
      'loadRationsList'
    ]),

    async loadData () {
      const filters = {
        farm: this.$route.params.farm_id,
        deleted: 'false'
      }
      await this.loadRationsList(filters)
      this.mixers = await this.loadMixersList(filters)
    },

    update (value) {
      this.item.mix_time = value * 60
      this.$emit('on-change')
    },

    onChange () {
      this.$emit('on-change')
    },

    sortIngredients () {
      this.item.schedule_ration.ingredients.forEach((element, index) => {
        element.ordering = index + 1
      })
      this.$emit('on-change')
    },

    openRationInfoDialog () {
      this.showRationInfoDialog = true
    },

    closeRationInfoDialog () {
      this.showRationInfoDialog = false
    }
  },

  computed: {
    ...mapGetters('rations', [
      'rationsList'
    ]),

    isWeightValid () {
      return this.totalWeight <= this.item.schedule_mixer.max_weight
    },

    weightPerHead () {
      const totalAnimals = this.animalCount

      if (totalAnimals === 0) return 0

      return this.totalWeight / totalAnimals
    },

    totalWeight () {
      if (!this.item.schedule_groups) return 0

      return this.item.schedule_groups.reduce((sum, group) => {
        return sum + (group.plan_weight || 0)
      }, 0)
    },

    animalCount () {
      if (!this.item.schedule_groups) return 0

      return this.item.schedule_groups.reduce((sum, group) => {
        return sum + (group.animal_count || 0)
      }, 0)
    }
  },

  watch: {
    mixer (value, prev) {
      if (prev !== null) {
        const data = {
          mixer: value.id,
          name: value.name,
          max_weight: value.max_weight
        }
        this.item.schedule_mixer = data
        this.$emit('on-change')
      }
    },

    ration (value, prev) {
      if (prev !== null) {
        const data = {
          ration: value.id,
          name: value.name
        }
        this.item.schedule_ration = data
        this.$emit('on-change', { reloadAfter: true })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .alert {
    color: #fff;
    padding: 12px 0;
    display: flex;
    text-align: center;
    align-items: center;
    justify-content: center;
    background: rgb(255, 87, 72);
    border-top: 1px solid rgba(255, 255, 255, 0.5);
  }

  .info-wrapper {
    border-top: 5px solid #E5E5E5;
  }

  .i-util-i {
    cursor: pointer;
  }

  .cols {
    display: flex;
    padding: 20px 10px;
  }

  .col {
    width: 33.3333%;
    padding: 0 10px;
  }

  .col-title {
    font-size: 14px;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .col-title .sub-controls {
    display: flex;
    align-items: center;
  }

  .col-title .sub-controls .label {
    color: #979797;
    margin-right: 20px;
    font-weight: 300;
  }

  .col-title .sub-controls .i-util-i {
    z-index: 2;
  }

  .col-title-offset {
    padding-left: 20px;
    padding-right: 20px;
  }

  .ration-select {
    border-bottom: 1px solid #e3e3e3;
  }

  .ration-detail {
    margin-top: 20px;
  }

  .ration-detail .item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .ration-detail .item .title {
    font-size: 14px;
  }

  .ration-detail .item .value {
    font-style: 15px;
  }

  .ration-detail .error {
    color: #F31500;
  }

  .ration-loader {
    padding: 0 2rem;
  }
</style>